import { Component, Input, OnInit, inject } from '@angular/core';
import {
  FormArray,
  FormControl,
  FormGroup,
  Validators
} from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { CampaignHashtag, CampaignHashtagPosts } from '@infrab4a/connect';
import { MessageService, PrimeNGConfig } from 'primeng/api';
import { FileUpload } from 'primeng/fileupload';
import { FileControllerService, PageableFilter } from 'src/app/admin-api';
import { CustomizationFormComponent } from 'src/app/components/customization/customization-form.component';
import { TableColumn } from 'src/app/components/table';
import { CampaignHashtagService } from 'src/app/connect-api/api/shop/campaign-hashtag.service';
import { CampaignHashtagPostsService } from 'src/app/connect-api/api/shop/campaign-hashtag-posts.service';
import { AppDialogService } from 'src/app/services/dialog.service';
import { ImageService } from 'src/app/services/image.service';
import { LoaderService } from 'src/app/services/loader.service';

@Component({
  selector: 'app-glam-campaigns-form',
  templateUrl: './glam-campaigns-form.component.html',
  styleUrls: ['./glam-campaigns-form.component.scss']
})
export class GlamCampaignsFormComponent
  extends CustomizationFormComponent
  implements OnInit
{
  @Input() campaignId: string;

  readonly titleMaxLength = 80;
  readonly descriptionMaxLength = 210;

  protected campaignHashtagPostsService = inject(CampaignHashtagPostsService);
  private campaignHashtagService = inject(CampaignHashtagService);
  private messageService = inject(MessageService);
  private title = inject(Title);

  campaign: CampaignHashtag | null = null;
  postsFixedFilters: PageableFilter[] = [];

  override form = new FormGroup({
    active: new FormControl<boolean>(true, { nonNullable: true }),
    title: new FormControl<string>('', {
      nonNullable: true,
      validators: [
        Validators.required,
        Validators.maxLength(this.titleMaxLength)
      ]
    }),
    hashtagCode: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.required]
    }),
    description: new FormControl<string>('', {
      nonNullable: true,
      validators: [
        Validators.required,
        Validators.maxLength(this.descriptionMaxLength)
      ]
    }),
    minimumGlampoints: new FormControl<number>(null, [
      Validators.required,
      Validators.min(0)
    ]),
    maximumGlampoints: new FormControl<number>(null, [
      Validators.required,
      Validators.min(0)
    ]),
    startDate: new FormControl<Date>(null),
    endDate: new FormControl<Date>(null),
    image: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.required]
    }),
    postsToShowInHome: new FormControl<number>(null, [Validators.min(0)]),
    topPosts: new FormControl<string[]>([], { nonNullable: true }),
    steps: new FormArray<FormControl<string>>([]),
    rules: new FormArray<FormControl<string>>([]),
    createdAt: new FormControl<Date>({ value: null, disabled: true }),
    updatedAt: new FormControl<Date>({ value: null, disabled: true })
  });

  protected postsCols = [
    new TableColumn.Builder()
      .setHeader('Conta')
      .setField('personAccount')
      .setType('text')
      .setCondition('contains')
      .setRouterLink(
        (post: CampaignHashtagPosts) =>
          `/configuracoes/glam/campaigns/${this.campaignId}/posts/${post.id}`
      )
      .build(),
    new TableColumn.Builder()
      .setHeader('Person ID')
      .setField('personId')
      .setType('number')
      .build(),
    new TableColumn.Builder()
      .setHeader('Status')
      .setField('status')
      .setType('text')
      .setCondition('contains')
      .build(),
    new TableColumn.Builder()
      .setHeader('Aprovado')
      .setField('approved')
      .setType('boolean')
      .setCondition('equals')
      .build(),
    new TableColumn.Builder()
      .setHeader('Nota')
      .setField('rating')
      .setType('number')
      .build(),
    new TableColumn.Builder()
      .setHeader('Consentimento')
      .setField('consent')
      .setType('boolean')
      .setFilter(false)
      .build(),
    new TableColumn.Builder()
      .setHeader('Criado em')
      .setField('createdAt')
      .setType('date')
      .setCondition('gte')
      .build()
  ];

  protected postsDropdownFilters = {
    approved: [
      { label: 'Todos', value: undefined },
      { label: 'Sim', value: true },
      { label: 'Não', value: false }
    ]
  };

  constructor(
    override router: Router,
    override imageService: ImageService,
    override config: PrimeNGConfig,
    override fileService: FileControllerService
  ) {
    super(router, config, imageService, fileService);
  }

  async ngOnInit(): Promise<void> {
    this.title.setTitle(
      this.campaignId ? 'Editar campanha' : 'Nova campanha'
    );
    await this.getDocument();
  }

  override async getDocument(): Promise<void> {
    if (!this.campaignId) {
      return;
    }

    this.postsFixedFilters = [
      {
        field: 'campaignId',
        value: this.campaignId,
        condition: 'equals'
      }
    ];

    LoaderService.showLoader();
    try {
      this.campaign = await this.campaignHashtagService.getCampaignById(
        this.campaignId
      );
      this.initializeForm();
    } catch (error) {
      AppDialogService.showErrorDialog(error, true);
    } finally {
      LoaderService.showLoader(false);
    }
  }

  override initializeForm(): void {
    const campaign = this.campaign;
    if (!campaign) {
      return;
    }

    this.form.patchValue({
      active: campaign.active ?? true,
      title: campaign.title ?? '',
      hashtagCode: campaign.hashtagCode ?? '',
      description: campaign.description ?? '',
      minimumGlampoints: campaign.minimumGlampoints,
      maximumGlampoints: campaign.maximumGlampoints,
      startDate: this.toDate(campaign.startDate),
      endDate: this.toDate(campaign.endDate),
      image: campaign.image ?? '',
      postsToShowInHome: campaign.postsToShowInHome,
      topPosts: campaign.topPosts ?? [],
      createdAt: this.toDate(campaign.createdAt),
      updatedAt: this.toDate(campaign.updatedAt)
    });

    this.replaceStringArray(this.steps, campaign.steps);
    this.replaceStringArray(this.rules, campaign.rules);
  }

  override async save(): Promise<void> {
    if (
      this.form.invalid ||
      this.isGlampointsRangeInvalid() ||
      this.isDateRangeInvalid()
    ) {
      this.form.markAllAsTouched();
      return;
    }

    const formValue = this.form.getRawValue();
    const campaign: Partial<CampaignHashtag> = {
      active: formValue.active,
      title: formValue.title,
      hashtagCode: formValue.hashtagCode,
      description: formValue.description,
      minimumGlampoints: Number(formValue.minimumGlampoints),
      maximumGlampoints: Number(formValue.maximumGlampoints),
      startDate: formValue.startDate,
      endDate: formValue.endDate,
      image: formValue.image,
      postsToShowInHome: formValue.postsToShowInHome,
      topPosts: (formValue.topPosts ?? []).filter((post) => post?.trim()),
      steps: formValue.steps.filter((step) => step?.trim()),
      rules: formValue.rules.filter((rule) => rule?.trim())
    };

    LoaderService.showLoader();
    try {
      if (this.campaignId) {
        await this.campaignHashtagService.updateCampaign({
          ...campaign,
          id: this.campaignId
        });
      } else {
        await this.campaignHashtagService.saveCampaign(campaign);
      }

      this.messageService.add({
        severity: 'success',
        summary: 'Salvo com sucesso',
        detail: 'As informações da campanha foram salvas.'
      });

      await this.afterSubmit();
      this.goToMenu();
    } catch (error) {
      AppDialogService.showErrorDialog(error);
    } finally {
      LoaderService.showLoader(false);
    }
  }

  get steps(): FormArray<FormControl<string>> {
    return this.form.controls.steps;
  }

  get rules(): FormArray<FormControl<string>> {
    return this.form.controls.rules;
  }

  addStep(): void {
    this.steps.push(this.createStringControl());
  }

  removeStep(index: number): void {
    this.steps.removeAt(index);
  }

  addRule(): void {
    this.rules.push(this.createStringControl());
  }

  removeRule(index: number): void {
    this.rules.removeAt(index);
  }

  invalidControl(controlName: string): boolean {
    const control = this.form.get(controlName);
    return !!control?.invalid && !!(control?.touched || control?.dirty);
  }

  isStepInvalid(index: number): boolean {
    const control = this.steps.at(index);
    return !!control?.invalid && !!(control?.touched || control?.dirty);
  }

  isRuleInvalid(index: number): boolean {
    const control = this.rules.at(index);
    return !!control?.invalid && !!(control?.touched || control?.dirty);
  }

  isGlampointsRangeInvalid(): boolean {
    const minimum = this.form.controls.minimumGlampoints.value;
    const maximum = this.form.controls.maximumGlampoints.value;

    if (minimum == null || maximum == null) {
      return false;
    }

    return Number(minimum) > Number(maximum);
  }

  isDateRangeInvalid(): boolean {
    const startDate = this.form.controls.startDate.value;
    const endDate = this.form.controls.endDate.value;

    if (!startDate || !endDate) {
      return false;
    }

    return startDate.getTime() > endDate.getTime();
  }

  async handleFileSelect(
    event: { files: File[] },
    inputName: string,
    expectedWidth: number,
    expectedHeight: number,
    maxWeight: number,
    pInput: FileUpload
  ) {
    this.fileNamePattern = `campanha-glam-${new Date().getTime()}.webp`;

    await this.upload(
      event,
      inputName,
      expectedWidth,
      expectedHeight,
      maxWeight,
      pInput
    );
  }

  goToMenu(): void {
    this.navigate('/configuracoes/glam/campaigns');
  }

  goToNewPost(): void {
    this.navigate(`/configuracoes/glam/campaigns/${this.campaignId}/posts/new`);
  }

  override get filePath(): string {
    return '/pages/campanhas/';
  }

  override get model() {
    return this.campaign;
  }

  override get modelId() {
    return this.campaign?.id || this.campaignId;
  }

  private createStringControl(value = ''): FormControl<string> {
    return new FormControl(value, {
      nonNullable: true
    });
  }

  private replaceStringArray(
    formArray: FormArray<FormControl<string>>,
    values?: string[]
  ): void {
    formArray.clear();
    (values ?? []).forEach((value) => {
      formArray.push(this.createStringControl(value));
    });
  }

  private toDate(value: Date | string | number | undefined): Date | null {
    if (!value) {
      return null;
    }

    const date = value instanceof Date ? value : new Date(value);
    return isNaN(date.getTime()) ? null : date;
  }
}
